import PublicLayout from '@/layouts/PublicLayout';
import { Box, Container, Grid, Card, CardContent, Typography, Paper } from '@mui/material';
import AboutImage from '@/assets/images/hero-image.png';
import { School, Psychology, Security, Analytics, 
        AccountCircle, Search, Assignment, Star} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from '@mui/lab';

const features = [
  {
    title: 'Smart Matching',
    description: 'AI-powered system that connects students with opportunities matching their interests and skills.',
    icon: Psychology
  },
  {
    title: 'Secure Platform',
    description: 'Enterprise-grade security ensuring student data protection and privacy compliance.',
    icon: Security
  },
  {
    title: 'Progress Tracking',
    description: 'Real-time tracking and analytics of volunteer hours and impact metrics.',
    icon: Analytics
  }
];

const steps = [
  {
    title: 'Create Profile',
    description: 'Students build their profile with interests, skills, and availability.',
    icon: AccountCircle
  },
  {
    title: 'Find Opportunities',
    description: 'Browse and search for volunteer positions that match your interests.',
    icon: Search
  },
  {
    title: 'Track Progress',
    description: 'Log hours and get verification from organizations.',
    icon: Assignment
  },
  {
    title: 'Build Your Portfolio',
    description: 'Track skills gained from each experience and build your resume.',
    icon: Analytics
  }
];

const AboutPage = () => {
  return (
    <PublicLayout>
      <Container 
        maxWidth="xl" 
        sx={{ 
          py: { xs: 4, md: 8 },
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          width: '100%'
        }}
      >
        {/* About Section with Timeline */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={5} 
            sx={{ 
              position: { xs: 'relative', md: 'sticky' }, // Only sticky on desktop
              top: { xs: 0, md: 100 },
              height: 'fit-content',
              mb: { xs: 4, md: 0 } // Add bottom margin on mobile
            }}
          >
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 800,
                  mb: 4,
                  background: 'linear-gradient(45deg, #6B46C1 30%, #4C51BF 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Our Journey to Transform Volunteering
              </Typography>
              <Box
                component="img"
                src={AboutImage}
                alt="About Dreamz Connect"
                sx={{
                  width: '100%',
                  borderRadius: '20px',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  transform: { xs: 'none', md: 'perspective(1000px) rotateY(-5deg)' }, // Only apply transform on desktop
                }}
              />
            </motion.div>
          </Grid>

          <Grid item xs={12} md={7}>
            <Timeline 
              position="alternate"
              sx={{
                [`& .MuiTimelineItem-root`]: {
                  minHeight: { xs: '200px', sm: '160px' },
                  '&:before': {
                    // Remove the alternate positioning padding on mobile
                    display: { xs: 'none', sm: 'block' }
                  }
                },
                // Make timeline left-aligned on mobile
                [`& .MuiTimelineItem-root:nth-of-type(even) .MuiTimelineContent-root`]: {
                  textAlign: { xs: 'left', sm: 'right' }
                },
                // Adjust connector height for better spacing
                [`& .MuiTimelineConnector-root`]: {
                  height: { xs: '140px', sm: '100px' },
                  width: { xs: '2px', sm: '3px' }
                },
                pl: { xs: 0, sm: 'inherit' }
              }}
            >
              {[
                {
                  title: 'Our Story',
                  year: '2024',
                  content: 'Founded with a vision to revolutionize how students engage with volunteer opportunities.',
                  icon: School
                },
                {
                  title: 'Our Vision',
                  year: 'Present',
                  content: 'Creating a world where every student can access meaningful volunteer experiences.',
                  icon: Psychology
                },
                {
                  title: 'Our Mission',
                  year: 'Future',
                  content: 'Empowering individuals to build their future through impactful community service.',
                  icon: Star
                },
              ].map((item, index) => (
                <TimelineItem key={index}>
                  <TimelineSeparator>
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      transition={{ delay: index * 0.2 }}
                    >
                      <TimelineDot sx={{ 
                        bgcolor: 'primary.main',
                        width: { xs: '48px', sm: '56px', md: '64px' },
                        height: { xs: '48px', sm: '56px', md: '64px' },
                        p: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 24px rgba(107, 70, 193, 0.2)',
                        background: 'linear-gradient(45deg, #6B46C1 30%, #4C51BF 90%)',
                        ml: { xs: 0, sm: 'inherit' }
                      }}>
                        <item.icon sx={{ 
                          fontSize: { xs: '24px', sm: '28px', md: '32px' },
                          color: 'white'
                        }} />
                      </TimelineDot>
                    </motion.div>
                    <TimelineConnector sx={{
                      width: { xs: '2px', sm: '3px' },
                      bgcolor: 'primary.main',
                      background: 'linear-gradient(to bottom, #6B46C1 30%, #4C51BF 90%)',
                      opacity: 0.5
                    }} />
                  </TimelineSeparator>
                  <TimelineContent>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.2 }}
                    >
                      <Paper
                        elevation={2}
                        sx={{
                          p: { xs: 2, sm: 2.5, md: 3 },
                          bgcolor: 'rgba(255,255,255,0.9)',
                          backdropFilter: 'blur(10px)',
                          borderRadius: 2,
                          border: '1px solid rgba(107, 70, 193, 0.1)',
                          transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: '0 12px 32px rgba(107, 70, 193, 0.15)',
                          },
                          ml: { xs: 2, sm: 'inherit' },
                          mr: { xs: 2, sm: 'inherit' }
                        }}
                      >
                        <Typography 
                          variant="overline" 
                          color="primary.main"
                          sx={{
                            fontWeight: 600,
                            letterSpacing: '0.1em',
                            fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }
                          }}
                        >
                          {item.year}
                        </Typography>
                        <Typography 
                          variant="h5" 
                          gutterBottom 
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                            mb: 1
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography 
                          variant="body1"
                          sx={{
                            color: 'text.secondary',
                            fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' },
                            lineHeight: { xs: 1.5, md: 1.6 }
                          }}
                        >
                          {item.content}
                        </Typography>
                      </Paper>
                    </motion.div>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          </Grid>
        </Grid>

        {/* How It Works Section */}
        <Box sx={{ py: { xs: 8, md: 12 } }}>
          <Typography
            variant="h2"
            align="center"
            sx={{
              mb: 8,
              fontWeight: 800,
              background: 'linear-gradient(45deg, #6B46C1 30%, #4C51BF 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            How It Works
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <Grid container spacing={4}>
              {steps.map((step, index) => (
                <Grid item xs={12} md={6} lg={3} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card 
                      sx={{ 
                        height: '100%',
                        transition: 'all 0.3s ease',
                        '&:hover': { 
                          transform: 'translateY(-10px)',
                          boxShadow: '0 12px 32px rgba(107, 70, 193, 0.15)'
                        },
                        bgcolor: 'rgba(255, 255, 255, 0.98)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(107, 70, 193, 0.1)',
                        borderRadius: '16px',
                        overflow: 'hidden'
                      }}
                    >
                      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          textAlign: 'center'
                        }}>
                          <Box 
                            sx={{ 
                              mb: 3,
                              width: { xs: '80px', md: '96px' },
                              height: { xs: '80px', md: '96px' },
                              background: 'linear-gradient(45deg, #6B46C1 30%, #4C51BF 90%)',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 8px 24px rgba(107, 70, 193, 0.2)',
                              p: 0
                            }}
                          >
                            <step.icon sx={{ 
                              fontSize: { xs: '40px', md: '48px' },
                              color: 'white'
                            }} />
                          </Box>
                          <Typography 
                            variant="h5" 
                            gutterBottom
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: '1.25rem', md: '1.5rem' },
                              mb: 2
                            }}
                          >
                            {step.title}
                          </Typography>
                          <Typography 
                            variant="body1" 
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: '0.875rem', md: '1rem' },
                              lineHeight: 1.6
                            }}
                          >
                            {step.description}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        {/* Features Section */}
        <Box sx={{ py: { xs: 8, md: 12 } }}>
          <Typography
            variant="h2"
            align="center"
            sx={{
              mb: 8,
              fontWeight: 800,
              background: 'linear-gradient(45deg, #6B46C1 30%, #4C51BF 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Platform Features
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card 
                    sx={{ 
                      height: '100%', 
                      transition: 'all 0.3s ease',
                      '&:hover': { 
                        transform: 'translateY(-10px)',
                        boxShadow: '0 12px 32px rgba(107, 70, 193, 0.15)'
                      },
                      border: '1px solid rgba(107, 70, 193, 0.1)',
                      borderRadius: 2
                    }}
                  >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Box
                          sx={{ 
                            background: 'linear-gradient(45deg, #6B46C1 30%, #4C51BF 90%)',
                            borderRadius: '12px',
                            p: 1.5,
                            mr: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(107, 70, 193, 0.2)'
                          }}
                        >
                          <feature.icon sx={{ fontSize: 32, color: 'white' }} />
                        </Box>
                        <Typography 
                          variant="h6" 
                          component="h3"
                          sx={{ 
                            fontWeight: 600,
                            color: '#2D3748'
                          }}
                        >
                          {feature.title}
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: '#4A5568',
                          lineHeight: 1.7
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </PublicLayout>
  );
};

export default AboutPage;